import fp from 'lodash/fp'
import { combine, withContext, withCallbacks } from 'react-hooks-combine'
import { withFormModel, withMobxAutorun } from 'shared/hooks'
import { spec, validators } from 'utils/validation'
import { ServicesContext } from 'services/context'

import BeneficiaryFiltersComponent from './component'
import {
  formatCategories,
  formatSubcategories,
  toSearchableCategory,
} from './helpers'

const { custom } = validators

// Storage key for persisting filter state
const MARKETING_FILTER_KEY = 'beneficiaries_marketing_promotion_filter';

// Load saved filter value from localStorage
const loadSavedMarketingFilter = () => {
  try {
    const saved = localStorage.getItem(MARKETING_FILTER_KEY);
    if (saved === 'true') return { id: 'true', name: 'Yes' };
    if (saved === 'false') return { id: 'false', name: 'No' };
  } catch (e) {
    // Silently handle localStorage errors - localStorage may be unavailable or restricted
  }
  return '';
};

export const BeneficiaryFilters = combine(
  withContext(
    'taxonomyService',
    ServicesContext,
    ({ taxonomyService }) => taxonomyService
  ),

  withFormModel(
    {
      search: {
        initialValue: '',
        spec: spec.flow(custom.valid),
      },
      categories: spec.flow(custom.valid),
      subCategories: spec.flow(custom.valid),
      marketingPromotionSelect: {
        initialValue: loadSavedMarketingFilter(),
        spec: spec.flow(custom.valid),
      },
    },
    { id: 'beneficiary-filters-form' }
  ),

  withCallbacks(
    {
      loadCategories:
        ({ taxonomyService }) =>
          () =>
            taxonomyService.queryBeneficiaryCategories().then(formatCategories),

      loadSubcategories:
        ({ taxonomyService }) =>
          () =>
            taxonomyService
              .queryBeneficiarySubcategories()
              .then(formatSubcategories),

      onSearchRequest: {
        deps: ['debounce', 'onSearch'],
        func: (_state, { debounce, onSearch }) => {
          return fp.isNumber(debounce)
            ? fp.debounce(debounce, onSearch)
            : onSearch
        },
      },
    },
    []
  ),

  withMobxAutorun(
    ({ firstRun, state: { form, onSearchRequest } }) => {
      const search = form.getControl('search')
      const categories = form.getControl('categories')
      const subCategories = form.getControl('subCategories')
      const marketingSelectControl = form.getControl('marketingPromotionSelect')

      const beneficiaryName = search.value
      const categoriesQuery = categories.value
      const subCategoriesQuery = subCategories.value

      // Extract id value from the selected option
      let marketingSelectValue = '';
      try {
        if (marketingSelectControl?.value) {
          if (typeof marketingSelectControl.value === 'object' && marketingSelectControl.value?.id) {
            marketingSelectValue = marketingSelectControl.value.id;
          } else {
            marketingSelectValue = String(marketingSelectControl.value);
          }
        }
  } catch (e) {
    // Error processing marketing selection - log for debugging
    console.warn('Error extracting marketing filter value:', e)
  }

  if (firstRun) {
        return
      }

      // Persist the filter value to localStorage
      try {
        if (marketingSelectValue) {
          localStorage.setItem(MARKETING_FILTER_KEY, marketingSelectValue);
        } else {
          localStorage.removeItem(MARKETING_FILTER_KEY);
        }
      } catch (e) {
        // Silently handle localStorage errors
      }

      // Build the query parameters
      const queryParams = {
        beneficiaryName,
        categories: toSearchableCategory(categoriesQuery),
        subCategories: toSearchableCategory(subCategoriesQuery),
      };

      // Check dropdown selection and add to query if needed
      if (marketingSelectValue === 'true') {
        queryParams.marketingPromotion = {
          value: true,
          operator: '='
        };
      } else if (marketingSelectValue === 'false') {
        queryParams.marketingPromotion = {
          value: false,
          operator: '='
        };
      }

      onSearchRequest(queryParams);
    },
    ['onSearchRequest']
  )
)(BeneficiaryFiltersComponent)
